// ── Welle D3b: ⋯-Menü der Widgets ──
// PC: Popover per Portal in <body> (Widgets liegen in transformierten Rasterzellen — ein
// fixed-Element darin würde eingesperrt). Tastatur: Pfeile, Pos1/Ende, Esc (Fokus zurück).
// Handy: LdSheet mit Größen-Skizzen, Nach oben/unten, Ausblenden, Board anpassen.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MIcon } from '../../../lib/micon';
import { LdSheet } from '../../../components/ui/LdSheet';
import type { Groesse, WidgetDef } from './layout-logik';
import { groessenStufen } from './layout-logik';

function Skizze({ w, h, max = 4 }: { w: number; h: number; max?: number }) {
  // Mini-Raster: 4 Spalten, belegte Zellen gefüllt
  const zeilen = 3; // feste Höhe, damit die Größen vergleichbar sind
  return (
    <span className="ld-wm__skizze" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${max}, 1fr)`, gridTemplateRows: `repeat(${zeilen}, 1fr)` }}>
      {Array.from({ length: max * zeilen }).map((_, i) => (
        <span key={i} className={i % max < w && Math.floor(i / max) < h ? 'ist' : ''} />
      ))}
    </span>
  );
}

type PcProps = {
  anker: HTMLButtonElement;
  titel: string;
  def: WidgetDef;
  aktuell: { w: number; h: number };
  onGroesse: (w: number, h: number) => void;
  onAusblenden: () => void;
  onAnpassen: () => void;
  onClose: () => void;
};

export function WidgetMenuPc({ anker, titel, def, aktuell, onGroesse, onAusblenden, onAnpassen, onClose }: PcProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const stufen = groessenStufen(def);

  // Position am ⋯-Knopf; beim Scrollen wandert das Menü mit (Scrollen beim Öffnen per
  // Tastatur, wenn der Knopf erst ins Bild rückt, darf es nicht schließen)
  const platzieren = useCallback(() => {
    const r = anker.getBoundingClientRect();
    const breite = 236;
    const hoehe = ref.current?.offsetHeight ?? 260;
    let top = r.bottom + 6;
    if (top + hoehe > window.innerHeight - 8) top = Math.max(8, r.top - hoehe - 6);
    const left = Math.min(Math.max(8, r.right - breite), window.innerWidth - breite - 8);
    setPos({ top, left });
  }, [anker]);
  useLayoutEffect(() => { platzieren(); }, [platzieren]);

  // Einmal fokussieren, sobald positioniert (nicht bei jedem Mitwandern)
  const fokussiert = useRef(false);
  useEffect(() => {
    if (!pos || fokussiert.current) return;
    fokussiert.current = true;
    const erstes = ref.current?.querySelector<HTMLElement>('[role^="menuitem"][aria-checked="true"]')
      ?? ref.current?.querySelector<HTMLElement>('[role^="menuitem"]');
    erstes?.focus();
  }, [pos]);

  // Außerhalb klicken oder Fenstergröße ändern schließt das Menü
  useEffect(() => {
    const weg = (e: Event) => {
      if (e.type === 'pointerdown' && (ref.current?.contains(e.target as Node) || anker.contains(e.target as Node))) return;
      onClose();
    };
    document.addEventListener('pointerdown', weg, true);
    window.addEventListener('resize', weg);
    window.addEventListener('scroll', platzieren, true);
    return () => {
      document.removeEventListener('pointerdown', weg, true);
      window.removeEventListener('resize', weg);
      window.removeEventListener('scroll', platzieren, true);
    };
  }, [anker, onClose, platzieren]);

  function schliessenMitFokus() { onClose(); anker.focus(); }

  function onKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); schliessenMitFokus(); }
    else if (e.key === 'Tab') { onClose(); }
  }

  return createPortal(
    <div ref={ref} className="ld-wm" role="menu" aria-label={`Optionen für ${titel}`} onKeyDown={onKeyDown}
      onPointerDown={e => e.stopPropagation()}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}>
      <div className="ld-wm__gruppe" role="group" aria-label="Größe">
        <span className="ld-wm__label" aria-hidden="true">Größe</span>
        {stufen.map(s => {
          const an = s.w === aktuell.w && s.h === aktuell.h;
          return (
            <button key={s.key} type="button" role="menuitemradio" aria-checked={an} className={`ld-wm__item${an ? ' ist-an' : ''}`}
              onClick={() => { if (!an) onGroesse(s.w, s.h); schliessenMitFokus(); }}>
              <Skizze w={s.w} h={s.h} />
              <span className="ld-wm__text">{s.label}</span>
              <span className="ld-wm__masse">{s.w} × {s.h}</span>
              {an && <MIcon name="check" size={16} className="ld-wm__check" />}
            </button>
          );
        })}
      </div>
      <div className="ld-wm__trenner" role="separator" />
      <button type="button" role="menuitem" className="ld-wm__item" onClick={() => { onClose(); onAusblenden(); }}>
        <MIcon name="visibility_off" size={18} /><span className="ld-wm__text">Ausblenden</span>
      </button>
      <button type="button" role="menuitem" className="ld-wm__item" onClick={() => { onClose(); onAnpassen(); }}>
        <MIcon name="dashboard_customize" size={18} /><span className="ld-wm__text">Board anpassen</span>
      </button>
    </div>,
    document.body,
  );
}

type HandyProps = {
  offen: boolean;
  titel: string;
  def: WidgetDef | null;
  groesse: Groesse;
  istErstes: boolean;
  istLetztes: boolean;
  onGroesse: (g: Groesse) => void;
  onHoch: () => void;
  onRunter: () => void;
  onAusblenden: () => void;
  onAnpassen: () => void;
  onClose: () => void;
};

export function WidgetSheetHandy({ offen, titel, def, groesse, istErstes, istLetztes, onGroesse, onHoch, onRunter, onAusblenden, onAnpassen, onClose }: HandyProps) {
  const erlaubt = def?.handy.erlaubt ?? [];
  return (
    <LdSheet open={offen} onClose={onClose} title={titel} maxWidth={480}>
      <div className="ld-wsh">
        <div className="ld-wsh__label" id="ld-wsh-groesse">Größe</div>
        <div className="ld-wsh__groessen" role="radiogroup" aria-labelledby="ld-wsh-groesse">
          {(['klein', 'gross'] as Groesse[]).map(g => {
            const darf = erlaubt.includes(g);
            const an = g === groesse;
            return (
              <button key={g} type="button" role="radio" aria-checked={an} disabled={!darf}
                className={`ld-wsh__groesse${an ? ' ist-an' : ''}`}
                onClick={() => { if (!an) onGroesse(g); }}>
                <span className={`ld-wsh__skizze ld-wsh__skizze--${g}`} aria-hidden="true"><span /><span /></span>
                <span>{g === 'klein' ? 'Klein' : 'Groß'}</span>
                {!darf && <span className="ld-wsh__nicht">nicht verfügbar</span>}
              </button>
            );
          })}
        </div>
        <div className="ld-wsh__liste">
          <button type="button" className="ld-wsh__zeile" disabled={istErstes} onClick={onHoch}>
            <MIcon name="arrow_upward" size={20} /><span>Nach oben</span>
          </button>
          <button type="button" className="ld-wsh__zeile" disabled={istLetztes} onClick={onRunter}>
            <MIcon name="arrow_downward" size={20} /><span>Nach unten</span>
          </button>
          <button type="button" className="ld-wsh__zeile" onClick={onAusblenden}>
            <MIcon name="visibility_off" size={20} />
            <span>Ausblenden<small>Zurückholen über „Board anpassen“</small></span>
          </button>
          <button type="button" className="ld-wsh__zeile" onClick={onAnpassen}>
            <MIcon name="dashboard_customize" size={20} /><span>Board anpassen</span>
          </button>
        </div>
      </div>
    </LdSheet>
  );
}
